import React, { useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import OptimusButton from '../../common/library/Button/Button';
import { TestIds } from '../../assets/testIds';

interface DeleteChatDialogProps {
  open: boolean;
  // Title of the chat being deleted, quoted back in the confirmation.
  chatTitle: string;
  onCancel: () => void;
  // Resolves once the chat is deleted; rejects if it could not be.
  onConfirm: () => Promise<void>;
}

/**
 * Confirms deleting one chat from the history (SN-825).
 *
 * Stays open while the delete is in flight and on a failure, so the member sees
 * the outcome rather than a dialog that closed on something that did not happen.
 */
export function DeleteChatDialog({
  open, chatTitle, onCancel, onConfirm,
}: DeleteChatDialogProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasError, setHasError] = useState(false);
  const titleId = 'coach-delete-chat-dialog-title';

  // Each opening starts clean: a failure from the last attempt does not belong
  // to the next chat.
  useEffect(() => {
    if (open) {
      setIsDeleting(false);
      setHasError(false);
    }
  }, [open]);

  const handleConfirm = async () => {
    setIsDeleting(true);
    setHasError(false);
    try {
      await onConfirm();
    } catch {
      setHasError(true);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (!isDeleting) {
      onCancel();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby={titleId}
      data-testid={TestIds.coach.history.deleteDialog}
      PaperProps={{
        sx: {
          borderRadius: theme.spacing(0.5),
          margin: { xs: theme.spacing(2), sm: theme.spacing(4) },
          maxWidth: theme.spacing(65),
        },
      }}
    >
      <DialogTitle
        id={titleId}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: theme.spacing(3, 3, 2, 3),
        }}
      >
        <Typography variant="headingMd" component="span" sx={{ color: theme.palette.grey[900] }}>
          {t('coach.history.deleteDialog.title')}
        </Typography>
        <IconButton
          data-testid={TestIds.coach.history.deleteDialogCloseButton}
          aria-label={`${t('coach.history.deleteDialog.close')}`}
          size="small"
          onClick={handleClose}
          disabled={isDeleting}
          sx={{ color: theme.palette.text.secondary }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ padding: theme.spacing(1, 3, 2, 3) }}>
        {/* escapeValue: false because React escapes this text itself - see the
            same note on the history row title (SN-988). */}
        <Typography variant="bodyMd" sx={{ color: theme.palette.grey[700] }}>
          {t('coach.history.deleteDialog.body', {
            title: chatTitle,
            interpolation: { escapeValue: false },
          })}
        </Typography>
        {hasError && (
          <Typography
            variant="labelSm"
            data-testid={TestIds.coach.history.deleteDialogError}
            sx={{ display: 'block', marginTop: theme.spacing(1.5), color: theme.palette.error.main }}
          >
            {t('coach.history.deleteDialog.error')}
          </Typography>
        )}
      </DialogContent>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: theme.spacing(1),
          padding: theme.spacing(1, 3, 3, 3),
        }}
      >
        <OptimusButton
          data-testid={TestIds.coach.history.deleteDialogCancelButton}
          customVariant="secondaryGray"
          buttonText={`${t('coach.history.deleteDialog.cancel')}`}
          fullWidth={false}
          disabled={isDeleting}
          onClick={handleClose}
        />
        <OptimusButton
          data-testid={TestIds.coach.history.deleteDialogConfirmButton}
          customVariant="destructive"
          buttonText={`${t('coach.history.deleteDialog.confirm')}`}
          fullWidth={false}
          disabled={isDeleting}
          onClick={handleConfirm}
        />
      </Box>
    </Dialog>
  );
}
